import { ReactNode, FC, useState, useEffect } from 'react'
import s from './SectionCarousel.module.sass'
import { Carousel } from '@shared/ui/Layout/Carousel'
import { SectionTitle } from '@shared/ui/Layout/SectionTitle'
import { BlockBoundary } from '../../../lib/components/BlockBoundary'
import { SectionCarouselContext, TypeCarouselState } from '@shared/ui/Layout/SectionCarousel/SectionCarouselContext'
import {
  SectionCarouselItems,
  SectionCarouselSkeletons,
} from '@shared/ui/Layout/SectionCarousel/ui/SectionCarouselItems'
import IconKuper from '@icons/kuper/icon-kuper.svg'
import { Title } from '@shared/ui/Typography/Title'
import { AdvDetailed } from '@features/Common/AdvDetailed'

interface Props {
  children: ReactNode[] | ReactNode
  countSlidesOnContainer?: 2 | 3 | 6
  title: string
  handleBeforeClickButtonMore?: () => boolean
  link?: string | object
  className?: string
  kuper?: {
    description: string
    token: string
  }
  skeleton?: boolean
}

type CarouselExtensions = {
  Items: typeof SectionCarouselItems
  Skeletons: typeof SectionCarouselSkeletons
}

const SectionCarousel: FC<Props & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>> &
  CarouselExtensions = ({
  children,
  countSlidesOnContainer,
  title,
  kuper,
  handleBeforeClickButtonMore,
  link,
  className = '',
  skeleton = false,
  ...props
}) => {
  const [context, setContext] = useState<TypeCarouselState>({
    countSlidesOnContainer: countSlidesOnContainer || 2,
    skeleton: false,
  })

  useEffect(() => {
    setContext({
      countSlidesOnContainer: countSlidesOnContainer || 2,
      skeleton,
    })
  }, [countSlidesOnContainer, skeleton])

  return (
    <BlockBoundary>
      <SectionCarouselContext.Provider value={[context, setContext]}>
        <div className={`${s.container} ${className}`} {...props}>
          <Carousel analytic={{ section_name: title }}>
            <div className={s.header}>
              <SectionTitle
                title={title}
                link={link}
                handleBeforeAction={handleBeforeClickButtonMore}
                skeleton={skeleton}
                titleComponent={
                  kuper && (
                    <div className={s['kuper-header']}>
                      <IconKuper className={s['kuper-icon']} />
                      <Title tag={'h2'} sizes='h2 h2 h2 h4' className={s.title}>
                        {title}
                      </Title>
                      <AdvDetailed className={s['kuper-info']}>
                        <AdvDetailed.Button className={s['kuper-info-button']} />
                        <AdvDetailed.Popup
                          title={'Реклама'}
                          description={kuper.description}
                          token={kuper.token}
                          className={s['kuper-info-popup']}
                        />
                      </AdvDetailed>
                    </div>
                  )
                }
              />

              {!skeleton && <Carousel.Controls />}
            </div>

            {children}
          </Carousel>
        </div>
      </SectionCarouselContext.Provider>
    </BlockBoundary>
  )
}

SectionCarousel.Items = SectionCarouselItems
SectionCarousel.Skeletons = SectionCarouselSkeletons

export default SectionCarousel
